import { deleteBackup, estimateMonthlyCost } from "@app/deploy";
import { useDispatch, useLoader, useSelector } from "@app/react";
import {
  backupRestoreUrl,
  databaseDetailUrl,
  operationDetailUrl,
} from "@app/routes";
import { schema } from "@app/schema";
import type { DeployBackup } from "@app/types";
import type { PaginateProps } from "@app/ui/hooks";
import cn from "classnames";
import { useMemo } from "react";
import type { ReactElement } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BannerMessages } from "../banner";
import { ButtonCreate, ButtonDestroy } from "../button";
import { CostEstimateTooltip, costColDef } from "../cost-estimate-tooltip";
import { ControlledDataTable, idColDef } from "../datatable";
import { DateTimeCell } from "../datatable/cells";
import type {
  DataTableCellRendererProps,
  DataTableColDef,
} from "../datatable/datatable";
import { Group } from "../group";
import { tokens } from "../tokens";

const BackupTypePill = ({
  manual,
  final,
}: { manual: boolean; final: boolean }): ReactElement => {
  const className = cn(
    "rounded-full border-2",
    "text-sm font-semibold ",
    "px-2 flex justify-between items-center w-fit",
  );
  let type = "";
  if (manual) {
    type = "Manual";
  } else if (final) {
    type = "Final";
  } else {
    type = "Auto";
  }

  return (
    <div
      className={cn(
        className,
        manual
          ? "bg-indigo-100 text-indigo-400 border-indigo-300"
          : "bg-lime-100 text-green-400 border-lime-300",
        final
          ? "!bg-orange-100 !text-brown !border-brown"
          : "bg-indigo-100 text-green-400 border-indigo-300",
      )}
    >
      <div>{type}</div>
    </div>
  );
};

const DeleteBackup = ({ envId, id }: { envId: string; id: string }) => {
  const dispatch = useDispatch();
  const action = deleteBackup({ id });
  const loader = useLoader(action);
  const onClick = () => {
    dispatch(action);
  };

  return (
    <ButtonDestroy
      envId={envId}
      variant="delete"
      size="sm"
      onClick={onClick}
      isLoading={loader.isLoading}
      requireConfirm
    >
      Delete
    </ButtonDestroy>
  );
};

const BackupCostCell = ({
  data: backup,
}: DataTableCellRendererProps<DeployBackup>) => {
  const rates = useSelector(schema.costRates.select);
  const cost = estimateMonthlyCost({ rates, backups: [backup] });
  return (
    <div className={tokens.type.darker}>
      <CostEstimateTooltip cost={cost} />
    </div>
  );
};

const BackupActionsCell = ({
  data: backup,
}: DataTableCellRendererProps<DeployBackup>) => {
  const navigate = useNavigate();
  const onRestore = () => {
    navigate(backupRestoreUrl(backup.id));
  };

  return (
    <div className="flex items-center gap-2">
      <ButtonCreate envId={backup.environmentId} onClick={onRestore} size="sm">
        Restore
      </ButtonCreate>
      <DeleteBackup id={backup.id} envId={backup.environmentId} />
    </div>
  );
};

const backupIdColDef: DataTableColDef<DeployBackup> = {
  field: "id",
  sortable: false,
  ...idColDef,
  initialWidth: 170,
  maxWidth: 170,
  cellRenderer: ({ data }: DataTableCellRendererProps<DeployBackup>) => (
    <div className={tokens.type["normal lighter"]}>
      {data.copiedFromId ? `Copy of ${data.copiedFromId}` : data.id}
    </div>
  ),
};

const databaseColDef: DataTableColDef<DeployBackup> = {
  field: "databaseHandle",
  headerName: "Database",
  sortable: false,
  cellRenderer: ({ data }: DataTableCellRendererProps<DeployBackup>) => (
    <Link
      className="text-black group-hover:text-indigo hover:text-indigo"
      to={databaseDetailUrl(data.databaseId)}
    >
      {data.databaseHandle}
    </Link>
  ),
};

const sizeColDef: DataTableColDef<DeployBackup> = {
  field: "size",
  headerName: "Size",
  sortable: false,
  cellRenderer: ({ data }: DataTableCellRendererProps<DeployBackup>) => (
    <div className="text-gray-900">{data.size} GB</div>
  ),
};

const regionColDef: DataTableColDef<DeployBackup> = {
  field: "awsRegion",
  headerName: "Region",
  sortable: false,
  cellRenderer: ({ data }: DataTableCellRendererProps<DeployBackup>) => (
    <div className="text-gray-900">{data.awsRegion}</div>
  ),
};

const createdAtColDef: DataTableColDef<DeployBackup> = {
  field: "createdAt",
  headerName: "Created At (UTC)",
  sortable: false,
  cellRenderer: ({ data }: DataTableCellRendererProps<DeployBackup>) => (
    <DateTimeCell date={data.createdAt} />
  ),
};

const creatorColDef: DataTableColDef<DeployBackup> = {
  field: "createdByEmail",
  headerName: "Creator",
  sortable: false,
  cellRenderer: ({ data }: DataTableCellRendererProps<DeployBackup>) => (
    <div className="text-gray-900">{data.createdByEmail}</div>
  ),
};

const operationIdColDef: DataTableColDef<DeployBackup> = {
  field: "createdFromOperationId",
  headerName: "Operation ID",
  sortable: false,
  initialHide: true,
  cellRenderer: ({ data }: DataTableCellRendererProps<DeployBackup>) => (
    <Link
      className="text-black group-hover:text-indigo hover:text-indigo"
      to={operationDetailUrl(data.createdFromOperationId)}
    >
      {data.createdFromOperationId}
    </Link>
  ),
};

const backupCostColDef: DataTableColDef<DeployBackup> = {
  ...costColDef,
  colId: "cost",
  sortable: false,
  cellRenderer: BackupCostCell,
};

const actionsColDef: DataTableColDef<DeployBackup> = {
  colId: "actions",
  headerName: "Actions",
  sortable: false,
  initialWidth: 190,
  suppressSizeToFit: true,
  resizable: false,
  cellRenderer: BackupActionsCell,
};

const typeColDef = (showFinal: boolean): DataTableColDef<DeployBackup> => ({
  colId: "type",
  headerName: "Type",
  sortable: false,
  cellRenderer: ({ data }: DataTableCellRendererProps<DeployBackup>) => (
    <div className="text-gray-900">
      <BackupTypePill manual={data.manual} final={showFinal} />
    </div>
  ),
});

export const DatabaseBackupsList = ({
  paginated,
  showDatabase = true,
  showFinal = false,
}: {
  paginated: PaginateProps<DeployBackup>;
  showDatabase?: boolean;
  showFinal?: boolean;
}) => {
  const loader = useLoader(deleteBackup);
  const data = paginated.data.filter((backup) => !backup?.deletedAt);

  const colDefs = useMemo<DataTableColDef<DeployBackup>[]>(
    () =>
      [
        backupIdColDef,
        showDatabase ? databaseColDef : null,
        typeColDef(showFinal),
        sizeColDef,
        regionColDef,
        createdAtColDef,
        creatorColDef,
        operationIdColDef,
        backupCostColDef,
        actionsColDef,
      ].filter(
        (colDef): colDef is DataTableColDef<DeployBackup> => colDef !== null,
      ),
    [showDatabase, showFinal],
  );

  return (
    <Group>
      <BannerMessages {...loader} />

      <ControlledDataTable<DeployBackup>
        rowData={data}
        colDefs={colDefs}
        isLoading={paginated.isLoading}
        searchable={false}
        page={paginated.page}
        totalPages={paginated.totalPages}
        totalItems={paginated.totalItems}
        onPrev={paginated.prev}
        onNext={paginated.next}
      />
    </Group>
  );
};
